<i18n src="./resources/locales.json"></i18n>

<template>
  <div :style="CSS_VARS" class="sms-input-field-container" :class="{ 'stick-to-bottom': stickToBottom }">
    <div class="counter-style"
      :class="{
        'max-limit': isTextOverflowed,
        'disabled-counter': disabled,
        'high-contrast': isHighContrastThemeActive
      }">
      <label>{{ textLimit }}</label>
    </div>
    <v-textarea
      ref="textfield"
      v-model="messageText"
      class="sms-input-field"
      :class="{
        'quick-message': quickMessage,
        'mms': attachedFile.hasFile,
        'high-contrast': isHighContrastThemeActive
      }"
      :auto-grow="true"
      :clearable="false"
      :rows="1"
      :label="placeholder"
      :loading="isLoading"
      :disabled="disabled"
      :no-resize="quickMessage"
      @change="onInputChange"
      @keydown.enter.prevent="sendMessage($event)"
      @focus="clearUnreadMessagesForActiveConversation"
      @paste="onPaste"
    >
    </v-textarea>
    <div class="attached-files-section scroller custom-scrollbar" v-if="attachedFile.hasFile">
      <v-row no-gutters v-if="attachedFile.isGif">
        <SmsGiphyItem :gif="attachedFile.file.sourceImage" isPreview />
      </v-row>
      <v-row no-gutters v-else>
        <div class="file-section">
            <span class="file-name-section">
              <v-icon>{{ attachedFile.icon || 'mdi-file-image' }}</v-icon>
              {{ attachedFile.file.name }}
            </span>
            <v-btn icon @click="deleteFile()" class="delete-file-icon">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
      </v-row>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { MAX_SIZE_KB } from '@/constants/attachments';
import SmsGiphyItem from '../SmsGiphyComponents/SmsGiphyItem';
import Message from '@/classes/v2/Message';
import ToastMixin from '@/mixins/ToastMixin';
import ThemeMixin from '@/mixins/ThemeMixin';

export default {
  name: 'SmsTextFieldComponent',

  components: { SmsGiphyItem },

  mixins: [ ToastMixin, ThemeMixin ],

  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    stickToBottom: {
      type: Boolean,
      default: false
    },
    quickMessage: {
      type: Boolean,
      default: false
    },
    messageInputLabel: {
      type: String,
      default: ''
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    attachedFile: {
      type: Object,
      default: () => {
        return {};
      }
    }
  },

  data() {
    return {
      messageText: '',
      textareaElement: null
    };
  },

  computed: {
    ...mapGetters('user', ['activeProvider']),
    CSS_VARS() {
      return {
        '--label-background': this.quickMessage ? 'var(--v-surface2-base)' : 'var(--v-surface-base)',
        '--input-max-height': this.quickMessage ? '150px' : '400px'
      };
    },

    maxTextLength() {
      return this.attachedFile.hasFile
        ? this.activeProvider.maxMmsMessageLength
        : this.activeProvider.maxSmsMessageLength;
    },

    unicodeText() {
      return Message.toUnicode(this.messageText);
    },

    charCount() {
      return this.unicodeText.length;
    },

    pages() {
      return Math.ceil(this.charCount / this.maxTextLength) || 1;
    },

    currentMax() {
      return this.pages * this.maxTextLength || this.maxTextLength;
    },

    isTextOverflowed() {
      return this.messageText !== null && this.pages > 10;
    },

    textLimit() {
      if (this.attachedFile.hasFile) {
        let attachmentSize = 0;

        if (this.attachedFile?.isGif) {
          attachmentSize = this.attachedFile?.file?.sourceImage.file.size || 0
        }
        else {
          attachmentSize = this.attachedFile?.file?.fileSRC.size || 0;
        }
        const mmsTotalByteLength = (new TextEncoder().encode(this.unicodeText)).byteLength + attachmentSize;
        const mmsTotalKbLength = Math.ceil(mmsTotalByteLength / 1024);
        const maxMMSSize = this.activeProvider.maxFileSizeKb + MAX_SIZE_KB.TEXT;

        // Bandwidth internally chunks the messages when sending to the carrier.
        if (!this.activeProvider.requiresSegmentation) {
          return `${mmsTotalKbLength}/${maxMMSSize}kb - ${this.charCount}`;
        }

        return `${mmsTotalKbLength}/${maxMMSSize}kb - ${this.charCount}/${this.currentMax} (${this.pages})`;
      } else {
        // Bandwidth internally chunks the messages when sending to the carrier.
        if (!this.activeProvider.requiresSegmentation) {
          return this.charCount;
        }

        // All other carriers for now
        return `${this.charCount}/${this.currentMax} (${this.pages})`;
      }
    },

    placeholder() {
      const defaultMessage = this.disabled ? this.$t('disabledMessage') : this.$t('sendMessagePlaceholder');
      return this.messageInputLabel ? this.messageInputLabel : defaultMessage;
    }
  },

  mounted() {
    const textField = this.$refs.textfield;
    if (textField) {
      const element = textField.$el;
      if (element) {
        this.textareaElement = element.querySelector('textarea');
      }
    }

    this.$root.$on('init-new-message', this.clearTextField);
  },

  beforeDestroy() {
    this.$root.$off('init-new-message');
  },

  methods: {
    ...mapActions('conversationsV2', ['clearUnreadMessagesForActiveConversation']),

    sendMessage(event) {
      if (!event.shiftKey) {
        this.$emit('sendMessage', this.messageText)
      }
    },

    showOverflowWarning() {
      this.showWarning(this.$t('notifications.textLimit', {
        type: this.attachedFile.hasFile ? 'MMS' : 'SMS',
        size: this.maxTextLength * 10
      }));
    },

    /**
     * Adds an emoji to the text input.
     *
     * @param emoji
     */
    addEmoji(emoji) {
      const cursorPosition = this.textareaElement.selectionStart;
      this.messageText = this.messageText.replace(new RegExp(`(?<=^.{${cursorPosition}})`), emoji);
      this.setCursorPosition(cursorPosition + emoji.length);
    },

    /**
     * Refocuses and resets cursor position
     *
     * @param cursorPosition
     * @returns {Promise<void>}
     */
    async setCursorPosition(cursorPosition) {
      this.$refs.textfield.focus();
      await this.$nextTick();
      this.textareaElement.setSelectionRange(cursorPosition, cursorPosition);
    },

    onInputChange() {
      const inputContainer = this.$refs.textfield.$el;
      const textarea = inputContainer.querySelector('textarea');
      const lines = (textarea.scrollHeight - 24) / 28;
      if (lines > 1 && !this.quickMessage) inputContainer.classList.add('multiple-lines');
      else inputContainer.classList.remove('multiple-lines');
    },

    initialStateTextArea(draftText) {
      this.messageText = draftText ? draftText : '';
      this.$refs.textfield.focus();
    },

    clearTextField() {
      this.messageText = '';
      this.$refs.textfield.focus();
    },

    focus() {
      this.$refs.textfield.focus();
    },

    deleteFile() {
      this.$root.$emit('remove-attached');
    },

    onPaste(event) {
      const newFile = event.clipboardData?.files[0];
      if (newFile)
        this.$root.$emit('on-load-file', newFile);
    }
  }
};
</script>

<style scoped>
.sms-input-field-container {
  position: relative;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
}

.sms-input-field-container.stick-to-bottom {
  position: absolute;
  bottom: 0;
}

.sms-input-field {
  border-radius: 8px;
  padding: 0;
  font-size: 14px;
  background-color: var(--v-surface-base);
  transition: border-radius 0.2s ease;
  margin: 0;
}

.sms-input-field.high-contrast {
  border: 1px solid var(--v-input-border-base);
}

.sms-input-field.quick-message {
  background-color: var(--v-surface2-base);
}

.attached-files-section {
  background-color: var(--v-surface-base);
  border: 1px solid var(--v-input-border-base);
  width: 100%;
  max-height: 180px;
  margin-top: 0;
  padding: 12px;
  border-radius: 0 0 8px 8px;
}

.scroller {
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  overflow-y: auto;
  overflow-x: hidden;
}

.counter-style {
  font-size: x-small;
  position: absolute;
  width: 100%;
  z-index: 1;
}

.counter-style > label {
  float: right;
  margin: 0;
  background-color: var(--label-background);
  box-shadow: -1px 1px 5px 0px var(--label-background);
  padding: 4px;
  border-top-right-radius: 8px;
}

.counter-style.high-contrast > label {
  position: relative;
  top: 1px;
  right: 1px;
}

.max-limit {
  color: var(--v-error-base);
}
</style>

<style>
.sms-input-field.quick-message .v-text-field__slot .v-label.v-label--active {
  margin-top: 11px;
}

.sms-input-field.quick-message .v-text-field__slot .v-label {
  margin-top: 9px;
  margin-left: 8px;
}

.sms-input-field .v-text-field__slot .v-label {
  margin-top: 12px;
  margin-left: 16px;
  font-size: 14px;
  background-color: var(--label-background);
  box-shadow: 1px 1px 5px 0px var(--label-background);
}

.sms-input-field .v-input__slot {
  margin: 0;
  overflow: hidden;
}

.sms-input-field .v-input__append-inner {
  margin: 2px;
}

.sms-input-field .v-icon.v-icon::after {
  transform: scale(1);
}

.sms-input-field.mms,
.sms-input-field.v-input--is-focused {
  border-radius: 8px 8px 0 0 !important;
}

.sms-input-field .v-text-field__details {
  display: none;
}

.sms-input-field.v-text-field > .v-input__control > .v-input__slot:before {
  border: none !important;
}

.sms-input-field.quick-message  textarea {
  padding: 16px 8px 8px;
}

.sms-input-field textarea {
  padding: 16px 16px 8px;
  min-height: 48px;
  max-width: 100%;
}

.sms-input-field .theme--light.v-text-field .v-input__control .v-input__slot:before {
  border-color: transparent !important;
}

.sms-input-field > div {
  transition: box-shadow 0.3s linear;
}

.sms-input-field.multiple-lines textarea {
  margin-top: 20px;
}

.sms-input-field.multiple-lines > div {
  box-shadow: 0 0 20px #000a;
}

.sms-input-field .v-text-field {
  border-radius: 8px;
  padding: 0;
  position: absolute;
  width: 100%;
  bottom: 0;
  margin: 0;
}

.sms-input-field textarea::-webkit-scrollbar { }

.sms-input-field textarea {
  max-height: var(--input-max-height);
}

.file-section {
  padding: 5px;
  background-color: var(--v-surface2-base);
  border: 1px solid var(--v-surface-lighten3);
}

.delete-file-icon {
  float: right;
  top: -5px;
}

.disabled-counter {
  opacity: 50%;
}
</style>
